import { Component, Input, OnInit, computed, inject, signal } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { BaseModalComponent } from '@card-software/library';
import { TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs';
import {
  CashRequest,
  CashRequestLine,
  CashRequestLineInsert,
  CashRequestManualEntryInsert,
} from '../../../../models/cash-request/cash-request';
import { CashRequestLineFormGroup, CashRequestManualEntryFormGroup } from '../../../../models/cash-request/cash-request-line-form';
import { PostingModule } from '../../../../models/financial-account/financial-account';
import { CashRequestProxyService } from '../../../../proxies/cash-request.proxy.service';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';

/**
 * Add a cash request line by hand, or edit the manual entries on one that already exists (#5135).
 *
 * <p>Until now a line could only be born from the document picker, which left everything without a
 * document — planillas, statutory obligations, the cash a week needs that no invoice has been raised for —
 * with no way in at all. This is that way in, and it deliberately covers both halves of the same job:
 * opening a NEW account on the request, and adding to an account documents already put there.</p>
 *
 * <p>The two halves save through different endpoints, and that is the point rather than an accident. A new
 * line goes to POST /lines, which builds it whole. An existing line goes to PUT /lines/{id}/manual-entries,
 * which replaces ONLY the manual set — so saving here can never disturb what the picker planned, and the
 * account is fixed, because the documents on that line were routed to it BY that account.</p>
 *
 * <p>Entries are a replace-set, not a delta: every row that survives is sent, each keeping the `entryId` it
 * arrived with. A row without one is new; a row left out is deleted. That is what the endpoint means by
 * "these are the manual entries".</p>
 */
@Component({
  selector: 'app-cash-request-line-modal',
  templateUrl: './cash-request-line-modal.component.html',
})
export class CashRequestLineModalComponent extends BaseModalComponent<CashRequest | null> implements OnInit {
  private readonly service = inject(CashRequestProxyService);
  private readonly translate = inject(TranslateService);

  /** The draft being planned. */
  @Input({ required: true }) cashRequestId!: string;

  /** The line being added to. Omitted (or null) to open a new account on the request. */
  @Input() line: CashRequestLine | null = null;

  readonly isLoading = signal(0);

  /** Set once in ngOnInit — the mode cannot change while the modal is open. */
  readonly isExistingLine = signal(false);

  /**
   * Only accounts a cash request may plan against (#5135).
   *
   * <p>Mirrors the server rule this PBI added to ApplyGlAccountAsync — an account another module controls
   * (an AR control account, say) belongs to that module and is reached through it. An account with no
   * declared owner is freely usable, which is nearly every expense and payroll account, so the practical
   * effect is to hide the handful that would have been rejected on save.</p>
   */
  readonly postingModule = PostingModule.CashManagement;

  readonly form = new FormGroup<CashRequestLineFormGroup>({
    glAccountReference: new FormControl<string | null>(null, [Validators.required]),
    glAccountNumber: new FormControl<string | null>(null),
    glAccountName: new FormControl<string | null>(null),
    description: new FormControl<string | null>(null),
    entries: new FormArray<FormGroup<CashRequestManualEntryFormGroup>>([]),
  });

  /** Document entries already on the line. Read-only here: they are the picker's, and this screen never
   *  touches them — shown so the running total below is not missing money the user can see on the row. */
  readonly documentTotal = signal(0);

  get entries(): FormArray<FormGroup<CashRequestManualEntryFormGroup>> {
    return this.form.controls.entries;
  }

  /** Live sum of what is typed. Rebuilt from valueChanges rather than computed(): a FormArray is not a
   *  signal, so nothing would re-read it. */
  readonly manualTotal = signal(0);

  /** What the line will come to once saved — the manual rows plus whatever documents are already on it. */
  readonly lineTotal = computed(() => this.manualTotal() + this.documentTotal());

  ngOnInit(): void {
    const line = this.line;
    this.isExistingLine.set(!!line?.lineId);

    if (line) {
      this.form.patchValue({
        glAccountReference: line.glAccount?.reference ?? null,
        glAccountNumber: line.glAccount?.number ?? null,
        glAccountName: this.accountName(line),
        description: line.description ?? null,
      });
      // The account is not the user's to change here — the documents on this line were routed to it.
      this.form.controls.glAccountReference.disable();
      this.documentTotal.set(line.documentTotal ?? 0);
      for (const entry of line.breakdown ?? []) {
        if (entry.sourceDocument?.reference) continue; // a document entry is the picker's, not ours
        this.entries.push(this.buildEntry(entry.entryId ?? null, entry.description ?? null, entry.functionalAmount));
      }
    }

    // A modal that opens on an empty table reads as broken; one open row says "type here".
    if (this.entries.length === 0) this.addEntry();

    this.entries.valueChanges.subscribe(() => this.recomputeTotal());
    this.recomputeTotal();
  }

  private buildEntry(
    entryId: string | null,
    description: string | null,
    amount: number | null,
  ): FormGroup<CashRequestManualEntryFormGroup> {
    return new FormGroup<CashRequestManualEntryFormGroup>({
      entryId: new FormControl<string | null>(entryId),
      // Required here though the API allows it empty: an entry with no text renders as "Manual entry" and
      // tells the approver nothing, and saying what the money is for is the whole job of this field.
      description: new FormControl<string | null>(description, [Validators.required]),
      amount: new FormControl<number | null>(amount, [Validators.required, Validators.min(0.01)]),
    });
  }

  private recomputeTotal(): void {
    this.manualTotal.set(this.entries.controls.reduce((sum, entry) => sum + (entry.controls.amount.value ?? 0), 0));
  }

  /** The localized account name off a loaded line — it arrives as a translation, not a string. */
  private accountName(line: CashRequestLine): string | null {
    const name = line.accountName;
    if (!name) return null;
    return (this.translate.currentLang === 'es' ? name.es : name.en) || name.en || name.es || null;
  }

  addEntry(): void {
    this.entries.push(this.buildEntry(null, null, null));
  }

  removeEntry(index: number): void {
    this.entries.removeAt(index);
    this.recomputeTotal();
  }

  /** True once removing the last manual row would leave the line with nothing at all — the API refuses it,
   *  and the right move is to delete the line from the row it sits on. */
  readonly wouldEmptyLine = computed(() => this.isExistingLine() && this.documentTotal() === 0);

  private t(key: string, fallback: string): string {
    const value = this.translate.instant(key);
    return value === key ? fallback : value;
  }

  onSave(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      GlobalMessageUtil.warn(
        this.t('CashRequest', 'Cash Request'),
        this.t('CashRequest_Line_Invalid', 'Pick an account and give every entry a description and an amount above zero.'),
      );
      return;
    }

    const entries: CashRequestManualEntryInsert[] = this.entries.controls.map((entry) => ({
      entryId: entry.controls.entryId.value,
      description: entry.controls.description.value,
      amount: entry.controls.amount.value ?? 0,
    }));

    if (entries.length === 0) {
      // Caught here rather than at the API so the message names the fix instead of the rule: on a line with
      // no documents, "no manual entries" IS "no line", and deleting it is the operation being asked for.
      GlobalMessageUtil.warn(
        this.t('CashRequest', 'Cash Request'),
        this.wouldEmptyLine()
          ? this.t('CashRequest_Line_WouldEmpty', 'Removing every entry would leave an empty line — delete the line instead.')
          : this.t('CashRequest_Line_NoEntries', 'Add at least one entry.'),
      );
      return;
    }

    const lineId = this.line?.lineId;
    // An existing line saves through the manual-entries route, which leaves its planned documents alone.
    const save$ = lineId
      ? this.service.updateManualEntries(this.cashRequestId, lineId, { entries })
      : this.service.addLine(this.cashRequestId, this.buildInsert(entries));

    this.isLoading.update((v) => v + 1);
    save$.pipe(finalize(() => this.isLoading.update((v) => v - 1))).subscribe({
      next: (response) => {
        if (!response.success || !response.data) return;
        GlobalMessageUtil.success(
          this.t('CashRequest', 'Cash Request'),
          lineId ? this.t('CashRequest_Line_Saved', 'Entries saved.') : this.t('CashRequest_Line_Added', 'Line added.'),
        );
        this.close({ action: 'Save', data: response.data });
      },
      // Stays open on failure: what was typed is the user's work, and closing would throw it away.
      error: () => undefined,
    });
  }

  private buildInsert(manualEntries: CashRequestManualEntryInsert[]): CashRequestLineInsert {
    const value = this.form.getRawValue();
    return {
      description: value.description,
      glAccount: { reference: value.glAccountReference, number: value.glAccountNumber },
      manualEntries,
    };
  }

  onClose(): void {
    this.close({ action: 'Cancel', data: null });
  }
}
